import { useOrganization, usePermissions, useQueryParams } from 'hooks';
import _ from 'lodash';
import ScheduleReminderType from 'types/enum/ScheduleReminderType';
import { QueryParam } from 'utils/constants/queryParam';
import { IScheduleReminderDetails } from '../types/types';

function useSchedulingDataFilter() {
  const organization = useOrganization();
  const queryParams = useQueryParams();
  const { hasPermissionToManage } = usePermissions();

  function filter(reminders: IScheduleReminderDetails[]) {
    const currentMemberId = queryParams.getParam(QueryParam.EMPLOYEE_ID);
    const currentMemberGroupId = queryParams.getParam(QueryParam.EMPLOYEE_GROUP_ID);
    const currentPositionId = queryParams.getParam(QueryParam.POSITION_ID);
    const currentProjectId = queryParams.getParam(QueryParam.PROJECT_ID);
    const currentProjectGroupId = queryParams.getParam(QueryParam.PROJECT_GROUP_ID);
    const currentCostCodeId = queryParams.getParam(QueryParam.COST_CODE_ID);
    const currentCostCodeGroupId = queryParams.getParam(QueryParam.COST_CODE_GROUP_ID);
    const currentEquipmentId = queryParams.getParam(QueryParam.EQUIPMENT_ID);
    const currentEquipmentCategoryId = queryParams.getParam(QueryParam.EQUIPMENT_CATEGORY_ID);
    const currentColors = queryParams.getParam(QueryParam.COLORS);
    const type = queryParams.getParam(QueryParam.TYPE) as ScheduleReminderType | null;
    const colorList: string[] | null = currentColors ? currentColors.split(',') : null;

    return reminders.filter((reminder) => {
      const payload = reminder.payload ? JSON.parse(reminder.payload) : undefined;

      if (type && reminder.type !== type) {
        return false;
      }

      if ((currentProjectId || currentCostCodeId || currentEquipmentId) && !payload) {
        return false;
      }

      // Can't do this on GraphQL because it's tied into a payload field which is a JSON string rather than actual fields
      if (currentMemberId && !_.some(reminder.links, (link) => link.member.id === currentMemberId)) {
        return false;
      }

      if (organization.trackCostCode && currentCostCodeId && payload.cost_code !== currentCostCodeId) {
        return false;
      }

      if (organization.trackEquipment && currentEquipmentId && payload.equipment !== currentEquipmentId) {
        return false;
      }

      if (currentProjectGroupId) {
        if (reminder.project?.depth === 1 && currentProjectGroupId !== reminder.project.projectGroupId) {
          return false;
        }

        if (
          reminder.project?.depth !== 1 &&
          currentProjectGroupId !==
            reminder.project?.ancestors?.find((ancestor) => ancestor.depth === 1)?.projectGroupId
        ) {
          return false;
        }
      }

      if (currentCostCodeGroupId && currentCostCodeGroupId !== reminder.costCode?.costCodeGroupId) {
        return false;
      }

      if (currentEquipmentCategoryId && currentEquipmentCategoryId !== reminder.equipment?.model.category.id) {
        return false;
      }

      if (currentColors && !colorList?.some((color) => color === reminder.color)) {
        return false;
      }

      if (currentProjectId) {
        if (!reminder.project) {
          return false;
        }

        if (currentProjectId === reminder.project.id) {
          return true;
        }

        const ancestorIds = reminder.project?.ancestors?.map((ancestor) => ancestor.id);
        return !!ancestorIds?.includes(currentProjectId);
      }

      return reminder.links.some((link) => {
        if (currentPositionId && link.member?.positionId !== currentPositionId) {
          return false;
        }

        if (!hasPermissionToManage(link.member, 'viewSchedule')) {
          return false;
        }

        if (currentMemberGroupId && link.member.memberGroupId !== currentMemberGroupId) {
          return false;
        }

        return true;
      });
    });
  }

  return filter;
}

export default useSchedulingDataFilter;
